import React, { useMemo } from 'react';
import { View } from 'react-native';
import { CardView, LineSeparator, rem, Spacer } from 'design-system';
import SectionHeader from 'design-system/SectionHeader';
import Text from 'design-system/Text';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { getPaymentMonthSeverity } from '../utils';
const strings = {
    sectionTitle: 'Payment history',
    subTitle: 'Paying on time each month can help with building your credit score',
};
const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const MONTHS_IN_YEAR = 12;
const SEVERITY_LABELS = {
    onTime: 'On time',
    late: 'Missed payment',
    defaulted: 'Defaulted',
    unknown: 'No data',
};
const LEGEND_ORDER = [
    'onTime',
    'late',
    'defaulted',
    'unknown',
];
const useChartStyles = () => {
    const styles = useStyles(styleSet);
    const getSegmentStyle = (severity) => {
        switch (severity) {
            case 'onTime':
                return styles.segmentFilled;
            case 'late':
                return styles.segmentLate;
            case 'defaulted':
                return styles.segmentDefault;
            default:
                return undefined;
        }
    };
    return { styles, getSegmentStyle };
};
const Chart = ({ statusHistory }) => {
    const { styles, getSegmentStyle } = useChartStyles();
    const sortedHistory = useMemo(() => [...statusHistory].sort((a, b) => a.year - b.year), [statusHistory]);
    return (<>
      <View style={styles.chartRow}>
        <View style={styles.rowYearContainer}/>
        <View style={[styles.itemsContainer, styles.itemsGap]}>
          {MONTHS.map((month, index) => (<View key={index} style={styles.itemContainer}>
              <Text Text-14 Gray>
                {month}
              </Text>
            </View>))}
        </View>
      </View>
      {sortedHistory.map(({ year, months }) => {
            const monthsByIndex = new Array(MONTHS_IN_YEAR).fill(undefined);
            months.forEach((m) => {
                const idx = m.month - 1;
                if (idx >= 0 && idx < MONTHS_IN_YEAR)
                    monthsByIndex[idx] = m;
            });
            return (<View style={styles.chartRow} key={year}>
            <View style={styles.rowYearContainer}>
              <Text Text-14 Gray numberOfLines={1}>
                {year}
              </Text>
            </View>
            <View style={[styles.itemsContainer, styles.itemsGap]}>
              {monthsByIndex.map((monthStatus, index) => (<View key={index} style={styles.itemContainer}>
                  <View style={[
                        styles.chartSegment,
                        index === 0 && styles.firstSegment,
                        index === MONTHS_IN_YEAR - 1 && styles.lastSegment,
                        getSegmentStyle(getPaymentMonthSeverity(monthStatus)),
                    ]}/>
                </View>))}
            </View>
          </View>);
        })}
    </>);
};
const ChartLegend = () => {
    const { styles, getSegmentStyle } = useChartStyles();
    return (<View style={styles.legendContainer}>
      {LEGEND_ORDER.map((severity) => (<View style={styles.legendRow} key={severity}>
          <View style={[
                styles.chartSegment,
                styles.legendSegment,
                getSegmentStyle(severity),
            ]}/>
          <Text TextThin-14 Secondary>
            {SEVERITY_LABELS[severity]}{' '}
          </Text>
        </View>))}
    </View>);
};
export const CreditScorePaymentHistoryChart = ({ statusHistory, }) => {
    const styles = useStyles(styleSet);
    return (<>
      <SectionHeader largeVerticalMargins horizontalMargins>
        {strings.sectionTitle}
      </SectionHeader>
      <CardView noMarginTop noMarginBottom withPadding>
        <Text TextThin-14 Secondary>
          {strings.subTitle}
        </Text>
        <LineSeparator style={styles.lineSeparator}/>
        <Chart statusHistory={statusHistory}/>
        <Spacer.H24 />
        <ChartLegend />
      </CardView>
    </>);
};
export default CreditScorePaymentHistoryChart;
const styleSet = createStyleSheets((colors) => ({
    lineSeparator: {
        marginVertical: rem(16),
        marginHorizontal: rem(-16),
    },
    chartRow: {
        flexDirection: 'row',
    },
    rowYearContainer: {
        width: rem(48),
        marginVertical: rem(4),
    },
    itemsContainer: {
        flexDirection: 'row',
        flex: 1,
        justifyContent: 'space-between',
    },
    itemsGap: {
        gap: rem(6),
    },
    itemContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    chartSegment: {
        width: '100%',
        height: rem(12),
        backgroundColor: colors.cards.transparentOnDark,
        borderRadius: rem(1),
    },
    firstSegment: {
        borderStartStartRadius: rem(6),
        borderBottomStartRadius: rem(6),
    },
    lastSegment: { borderEndEndRadius: rem(6), borderTopEndRadius: rem(6) },
    segmentFilled: {
        backgroundColor: colors.elements.brand,
    },
    segmentLate: {
        backgroundColor: colors.elements.statusInformational,
    },
    segmentDefault: {
        backgroundColor: colors.elements.statusNegative,
    },
    legendContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        columnGap: rem(16),
        rowGap: rem(4),
    },
    legendRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(8),
    },
    legendSegment: {
        width: rem(20),
    },
}));
